import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { v3Api } from '../../hooks/useApi';

/**
 * Debounced typeahead picker for any entity type supported by
 * /api/comparison/search (Tenant, Property, Category, Subcategory,
 * Group, Floor, Zone, Vertical).
 *
 * Stores { id, label } so the chart legend can render a human-readable
 * name regardless of whether the user typed the underlying ObjectId.
 *
 * The results menu is PORTALLED to document.body and positioned with
 * `position: fixed` against the input's rect. The picker lives inside a
 * `.v3-card` which has `overflow: hidden` + `backdrop-filter` (its own
 * stacking context) — an in-flow absolute menu would be clipped at the card
 * edge (the "stripped bottom") and sit under the next card. The portal
 * escapes both. Background is opaque (base + elevated) so it never shows the
 * content behind — `--v3-bg-surface` is only 4% opaque in dark mode.
 *
 * Props:
 *   type:   one of the supported entity types
 *   value:  { id, label } | { id: '', label: '' }
 *   onChange(next): emits the new {id, label}
 */
export default function EntitySearchPicker({ type, value, onChange, placeholder }) {
    const { t } = useTranslation();
    const [query, setQuery] = useState(value?.label || '');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [coords, setCoords] = useState(null);
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);
    const debounceRef = useRef(null);

    // Sync external value → input text (e.g. when URL prefill or row clone hydrates)
    useEffect(() => {
        setQuery(value?.label || '');
    }, [value?.id, value?.label]);

    // Position the fixed menu directly under the input.
    const reposition = useCallback(() => {
        const el = inputRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
    }, []);

    // Close on outside click — the menu is portalled, so it isn't inside
    // wrapRef; check the menu node too so clicking a result doesn't count as
    // "outside".
    useEffect(() => {
        const onDoc = (e) => {
            if (wrapRef.current && wrapRef.current.contains(e.target)) return;
            if (menuRef.current && menuRef.current.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    // Keep the fixed menu glued to the input while open (scroll/resize).
    useEffect(() => {
        if (!open) return;
        reposition();
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [open, reposition]);

    // Debounced fetch
    useEffect(() => {
        if (!open) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await v3Api.comparison.search(type, query, 20);
                setResults(Array.isArray(res?.data) ? res.data : []);
            } catch (_) {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 280);
        return () => clearTimeout(debounceRef.current);
    }, [query, type, open]);

    const pick = (it) => {
        onChange({ id: it.id, label: it.label });
        setQuery(it.label);
        setOpen(false);
    };

    const menu = open && coords ? createPortal(
        <div ref={menuRef} style={{ ...dropdown, top: coords.top, left: coords.left, width: coords.width }}>
            {loading && <div style={hint}>{t('Searching…')}</div>}
            {!loading && results.length === 0 && <div style={hint}>{t('No matches')}</div>}
            {!loading && results.map((it) => (
                <div
                    key={`${it.id}-${it.label}`}
                    onMouseDown={(e) => { e.preventDefault(); pick(it); }}
                    style={row}
                >
                    <div style={{ fontSize: 13, color: 'var(--v3-text-primary)' }}>{it.label}</div>
                    {it.sub && <div style={{ fontSize: 11, color: 'var(--v3-text-muted)' }}>{it.sub}</div>}
                </div>
            ))}
        </div>,
        document.body
    ) : null;

    return (
        <div ref={wrapRef} style={wrap}>
            <input
                ref={inputRef}
                type="text"
                value={query}
                placeholder={placeholder || t('Search {{type}}…', { type: type.toLowerCase() })}
                onFocus={() => { setOpen(true); reposition(); }}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                style={input}
            />
            {menu}
        </div>
    );
}

const wrap = { position: 'relative', flex: 1, minWidth: 180 };
const input = {
    width: '100%',
    padding: '8px 12px',
    background: 'var(--v3-bg-surface)',
    border: '1px solid var(--v3-glass-border)',
    borderRadius: 8,
    color: 'var(--v3-text-primary)',
    fontSize: 13,
    outline: 'none',
};
const dropdown = {
    position: 'fixed',
    maxHeight: 280,
    overflowY: 'auto',
    // Opaque, theme-aware: solid base with the translucent elevated tint on top,
    // so nothing behind the menu shows through in either dark or light theme.
    background: 'linear-gradient(var(--v3-bg-elevated), var(--v3-bg-elevated)), var(--v3-bg-base)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1px solid var(--v3-glass-border)',
    borderRadius: 8,
    boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
    zIndex: 9999,
};
const row = {
    padding: '8px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid var(--v3-glass-border)',
};
const hint = { padding: '10px 12px', fontSize: 12, color: 'var(--v3-text-muted)' };
